import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BackLinkWithText } from "../../components/BackLink";
import { Button, ButtonWrap, Submit } from "../../components/CTA";
import { CancelIcon } from "../../components/CancelIcon";
import { Card } from "../../components/Card";
import { Chevron } from "../../components/Chevron";
import DetailList from "../../components/DetailList";
import { Dropdown } from "../../components/Dropdown";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { Input } from "../../components/Input";
import Loading from "../../components/Loading";
import { Modal } from "../../components/Modal";
import { InlineAddBtn, InlineBtn } from "../../components/NewButton";
import { Text } from "../../components/Text";
import { View } from "../../components/View";
import { CheckCircle } from "../../components/icons/CheckCircle";
import { TrashIcon } from "../../components/icons/TrashIcon";
import { MOBILE_BREAKPOINT } from "../../config";
import {
  BrandLocationQuery,
  ExcludedHoursInput,
  OpeningHoursInput,
  useBrandLocationQuery,
  useDeleteLocationMutation,
  useUpdateLocationMutation,
} from "../../graphql/generated";
import { queryKeys } from "../../graphql/queryKeys";
import useAnalytics from "../../hooks/useAnalytics";
import useGqlClient from "../../hooks/useGqlClient";
import { authSelectors } from "../../store/auth/selector";
import { styled } from "../../styles";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 600px;
  text-align: left;
  margin: 0 ${(p) => p.theme.spacing.l};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-top: 0px;
  }
`;

const StyledCard = styled(Card)`
  padding: ${(p) => p.theme.spacing.xl};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.l};
  }

  overflow: visible;
`;

const OptionsToggleWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export interface MatchParams {
  page: string;
}

export const BrandLocation = () => {
  return (
    <Wrap>
      <BackLinkWithText margin="0 0 0" to="/b/locations" text="Locations" />
      <LocationDetails />
    </Wrap>
  );
};

const LocationDetails = () => {
  const { track } = useAnalytics();
  const client = useGqlClient();
  let { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const [excludedHours, setExcludedHours] = useState<ExcludedHoursInput[]>([]);
  const [openingHours, setOpeningHours] = useState<OpeningHoursInput[]>([]);
  const [editingOpeningHours, setEditingOpeningHours] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const account = useSelector(authSelectors.account);

  const brand = useSelector(authSelectors.activeBrand);
  const domain = account
    ? account.email.substring(account?.email.lastIndexOf("@") + 1)
    : "example.com";
  const brandQuery = useBrandLocationQuery(
    client,
    { id },
    {
      retry: false,
    }
  );
  const updateLocation = useUpdateLocationMutation(client, {});

  const deleteLocation = useDeleteLocationMutation(client, {});

  const { isLoading, data, isError } = brandQuery;

  useEffect(() => {
    if (!data) {
      return;
    }

    setExcludedHours(data.location.excludedHours);
    setOpeningHours(
      data.location.openingHours.map((ohs) => {
        // Handle empty or invalid time values
        if (
          ohs.openHour === undefined ||
          ohs.closeHour === undefined ||
          ohs.openMinute === undefined ||
          ohs.closeMinute === undefined
        ) {
          // Default to reasonable business hours if times are missing
          return {
            dayOfWeek: ohs.dayOfWeek,
            startHour: 9,
            startMinute: 0,
            endHour: 17,
            endMinute: 0,
          };
        }

        const startHour = ohs.openHour;
        const startMinute = ohs.openMinute;
        let endHour = ohs.closeHour;
        let endMinute = ohs.closeMinute;

        // Validate hour and minute values
        if (
          startHour < 0 ||
          startHour > 23 ||
          startMinute < 0 ||
          startMinute > 59 ||
          endHour < 0 ||
          endHour > 23 ||
          endMinute < 0 ||
          endMinute > 59
        ) {
          return {
            dayOfWeek: ohs.dayOfWeek,
            startHour: 9,
            startMinute: 0,
            endHour: 17,
            endMinute: 0,
          };
        }

        return {
          dayOfWeek: ohs.dayOfWeek,
          startHour,
          startMinute,
          endHour,
          endMinute,
        };
      })
    );

    if (data.location.openingHours.length === 0) {
      Sentry.captureException(`${data.location.id} has no opening hours`);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Wrap>
        <Loading defer />
      </Wrap>
    );
  }

  if (isError || !data) {
    return (
      <Wrap>
        <H3>Oops</H3>

        <Text margin={"0 0 xl 0"}>
          Couldn't load this location, please try again later.
        </Text>
      </Wrap>
    );
  }

  const defaultExcludedHours: ExcludedHoursInput | undefined =
    openingHours.length > 0
      ? {
          startHour: openingHours[0].startHour,
          startMinute: openingHours[0].startMinute,
          endHour: openingHours[0].endHour,
          endMinute: openingHours[0].endMinute,
          dayOfWeek: openingHours[0].dayOfWeek,
        }
      : undefined;

  // Utility to compute earliest open and latest close times from openingHours
  function getEarliestOpenAndLatestClose(openingHours: OpeningHoursInput[]) {
    let earliestStartHour = 23;
    let earliestStartMinute = 59;
    let latestEndHour = 0;
    let latestEndMinute = 0;

    for (const oh of openingHours) {
      if (
        oh.startHour < earliestStartHour ||
        (oh.startHour === earliestStartHour &&
          oh.startMinute < earliestStartMinute)
      ) {
        earliestStartHour = oh.startHour;
        earliestStartMinute = oh.startMinute;
      }
      if (
        oh.endHour > latestEndHour ||
        (oh.endHour === latestEndHour && oh.endMinute > latestEndMinute)
      ) {
        latestEndHour = oh.endHour;
        latestEndMinute = oh.endMinute;
      }
    }
    return {
      earliestStartHour,
      earliestStartMinute,
      latestEndHour,
      latestEndMinute,
    };
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Transform excludedHours for 'Everyday' before saving
    const {
      earliestStartHour,
      earliestStartMinute,
      latestEndHour,
      latestEndMinute,
    } = getEarliestOpenAndLatestClose(openingHours);
    const transformedExcludedHours = excludedHours.map((eh) => {
      if (eh.dayOfWeek === "Everyday") {
        return {
          ...eh,
          startHour: earliestStartHour,
          startMinute: earliestStartMinute,
          endHour: latestEndHour,
          endMinute: latestEndMinute,
        };
      }
      return eh;
    });

    updateLocation.mutate(
      {
        id,
        name: fd.get("name") as string,
        email: fd.get("email") as string,
        dmnVenueId: fd.get("dmnVenueId")
          ? (fd.get("dmnVenueId") as string)
          : undefined,
        excludedHours: transformedExcludedHours,
        openingHours,
      },
      {
        onError: (e) => {
          setError(e as string);
        },
        onSuccess: (res) => {
          toast.success("Location updated");
          track("Edit location", {
            id: res.updateLocation?.id ? res.updateLocation.id : undefined,
            brand: brand ? brand.name : undefined,
          });
          queryClient.resetQueries([queryKeys.brandLocations], {
            exact: false,
          });
          queryClient.resetQueries([queryKeys.brandLocation], {
            exact: false,
          });
          history.push("/b/locations");
        },
      }
    );
  };

  return (
    <>
      {error && <Text>Error updating</Text>}

      <Modal isOpen={showDeleteModal} setIsOpen={setShowDeleteModal}>
        <H3 margin=" 0 0">Permanently Delete Location</H3>
        <Text margin="l 0 xl">
          This action is irreversible. For temporary closures, simply unassign
          the location from your live listings instead.
        </Text>
        <ButtonWrap margin="0">
          <Button
            size="s"
            onClick={() => setShowDeleteModal(false)}
            margin="0 0 0 0"
            buttonType="secondary"
          >
            Cancel
          </Button>
          <Button
            size="s"
            buttonType="destructive"
            onClick={() =>
              deleteLocation.mutate(
                {
                  locationId: id,
                },
                {
                  onError: (e) => {
                    setError(e as string);
                  },
                  onSuccess: (res) => {
                    toast.success("Location deleted");
                    track("Delete location", {
                      id: res.deleteLocation?.id
                        ? res.deleteLocation.id
                        : undefined,
                      brand: brand ? brand.name : undefined,
                    });
                    queryClient.resetQueries([queryKeys.brandLocations], {
                      exact: false,
                    });
                    queryClient.resetQueries([queryKeys.brandLocation], {
                      exact: false,
                    });
                    history.push("/b/locations");
                  },
                }
              )
            }
          >
            {deleteLocation.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </ButtonWrap>
      </Modal>

      <form onSubmit={onSubmit}>
        <StyledCard margin="l 0 xl">
          <Flex
            margin="0 0 l"
            align="center"
            justify="space-between"
            direction="row"
          >
            <H2 margin="0">Edit Location</H2>
            <TrashWrap onClick={() => setShowDeleteModal(true)}>
              <TrashIcon height={16} width={16} colorPreset="secondary" />
            </TrashWrap>
          </Flex>
          <Input
            label="Name"
            defaultValue={data.location.name}
            name="name"
            margin="0 0 l 0"
          />
          <Input
            margin="0 0 l 0"
            label="Email"
            defaultValue={data.location.email}
            name="email"
            placeholder={`e.g. borough@${domain}`}
            help="Add a location email to let them know about confirmed bookings"
          />

          <Input
            margin="0 0 l 0"
            label="Phone"
            defaultValue={data.location.phone}
            name="phone"
          />

          {data.location.instagramHandle ? (
            <div>
              <Text weight="semi" margin="s 0 s">
                Instagram
              </Text>

              <View margin="0 0 m">
                <div style={{ position: "relative" }}>
                  <AbsoluteWrap>
                    <CheckCircle checked={true} />
                  </AbsoluteWrap>
                  <Input
                    margin="0 0 s 0"
                    isDisabled={true}
                    value={`@${data.location.instagramHandle}`}
                    name="handle"
                  />
                </div>
              </View>
            </div>
          ) : null}

          {brand && brand.dmnEnabled ? (
            <>
              <Input
                margin="0 0 l 0"
                label="Access Collins site ID"
                defaultValue={data.location.dmnVenueId}
                name="dmnVenueId"
                placeholder={`eg. 512b2028d5d190d2978ca3b8`}
                help=" if you need help finding it"
                helpLink="https://accessgroup.my.site.com/Support/s/article/Access-Collins-Where-can-I-find-the-site-ID"
              />
            </>
          ) : null}

          <Flex margin="xl 0 0 0" justify="space-between" align="center">
            <div>
              <Text margin="0" weight="semi">
                Opening Hours
              </Text>
            </div>
            <InlineBtn
              label={editingOpeningHours ? "Save Hours" : "Edit Hours"}
              onClick={() => {
                setEditingOpeningHours(!editingOpeningHours);
              }}
            />
          </Flex>
          {editingOpeningHours ? (
            <>
              {openingHours.map((ehs, i) => {
                return (
                  <OpeningHours
                    location={data.location}
                    index={i}
                    key={`${ehs.dayOfWeek}-${ehs.startHour}-${ehs.startMinute}-${ehs.endHour}-${ehs.endMinute}`}
                    defaultValue={ehs}
                    onChange={(index, ehs) => {
                      const newOpeningHours = [...openingHours];
                      newOpeningHours.splice(index, 1);
                      newOpeningHours.splice(index, 0, ehs);

                      setOpeningHours(newOpeningHours);
                    }}
                    onRemove={(index) => {
                      const newOpeningHours = [...openingHours];
                      newOpeningHours.splice(index, 1);

                      setOpeningHours(newOpeningHours);
                    }}
                  />
                );
              })}
              <InlineAddBtn
                label="Add Hours"
                margin="m 0 m 0"
                onClick={() => {
                  const newOpeningHours = [...openingHours];
                  newOpeningHours.push({
                    dayOfWeek: "Monday",
                    startHour: 10,
                    startMinute: 0,
                    endHour: 18,
                    endMinute: 0,
                  });
                  setOpeningHours(newOpeningHours);
                }}
              />
            </>
          ) : (
            <DetailList
              margin="s 0 xl 0"
              fields={openingHours.map((ohs, i) => ({
                label: ohs.dayOfWeek,
                value: `${ohs.startHour < 10 ? "0" : ""}${ohs.startHour}:${
                  ohs.startMinute
                }${ohs.startMinute < 10 ? "0" : ""} - ${
                  ohs.endHour < 10 ? "0" : ""
                }${ohs.endHour}:${ohs.endMinute}${
                  ohs.endMinute < 10 ? "0" : ""
                }`,
              }))}
            />
          )}

          <View margin="m 0 0">
            {showAdvancedOptions ? (
              <OptionsToggleWrap onClick={() => setShowAdvancedOptions(false)}>
                <Chevron colorPreset="link" direction="down" />
                <Text weight="semi" colorPreset="link" margin="0 0 0 s">
                  Hide advanced options
                </Text>
              </OptionsToggleWrap>
            ) : (
              <OptionsToggleWrap onClick={() => setShowAdvancedOptions(true)}>
                <Chevron colorPreset="link" />
                <Text weight="semi" colorPreset="link" margin="0 0 0 s">
                  Show advanced options
                </Text>
              </OptionsToggleWrap>
            )}
            {showAdvancedOptions ? (
              <>
                <Text margin="xl 0 0 0" weight="semi">
                  Excluded Hours
                </Text>
                <Text size="s" colorPreset="secondary" margin="xs 0 0 0">
                  Block off certain hours of the week to prevent influencers
                  applying
                </Text>
                {excludedHours.map((ehs, i) => {
                  return (
                    <ExcludedHours
                      openingHours={openingHours}
                      index={i}
                      key={`${ehs.dayOfWeek}-${i}-${ehs.startHour}-${ehs.endHour}`}
                      defaultValue={ehs}
                      onChange={(index, ehs) => {
                        const newExcludedHours = [...excludedHours];
                        newExcludedHours.splice(index, 1);
                        newExcludedHours.splice(index, 0, ehs);

                        setExcludedHours(newExcludedHours);
                      }}
                      onRemove={(index) => {
                        setExcludedHours(
                          excludedHours.filter((_, i) => i !== index)
                        );
                      }}
                    />
                  );
                })}
                <InlineAddBtn
                  label="Add Hours"
                  margin="m 0 0"
                  onClick={() => {
                    const newExcludedHours = [...excludedHours];
                    defaultExcludedHours &&
                      newExcludedHours.push(defaultExcludedHours);
                    setExcludedHours(newExcludedHours);
                  }}
                />
              </>
            ) : null}
          </View>
        </StyledCard>

        <Submit
          margin="xl 0 xl 0"
          type="submit"
          value={updateLocation.isLoading ? "Saving..." : "Save"}
        />
      </form>
    </>
  );
};

interface ExcludedHoursProps {
  defaultValue: ExcludedHoursInput;
  openingHours: OpeningHoursInput[];
  onChange: (index: number, ehs: ExcludedHoursInput) => void;
  onRemove: (index: number) => void;
  index: number;
}

export function ExcludedHours(props: ExcludedHoursProps) {
  const { onChange, index } = props;
  const [selectedDay, setSelectedDay] = useState("");
  const [startHour, setStartHour] = useState<number | undefined>();
  const [endHour, setEndHour] = useState<number | undefined>();
  const [startMinute, setStartMinute] = useState<number | undefined>();
  const [endMinute, setEndMinute] = useState<number | undefined>();
  const [startTimeLabel, setStartTimeLabel] = useState<string>("");
  const [endTimeLabel, setEndTimeLabel] = useState<string>("");

  // Helper function to format time strings for display
  const formatTimeString = (hour: number, minute: number) => {
    return `${hour < 10 ? "0" : ""}${hour}:${minute}${
      minute < 10 || minute === 0 ? "0" : ""
    }`;
  };

  // Find the opening hours for the selected day
  const getDayOpeningHours = (day: string): OpeningHoursInput | undefined => {
    if (day === "Everyday") return undefined;

    for (const oh of props.openingHours) {
      if (oh.dayOfWeek === day) {
        return oh;
      }
    }
    return undefined;
  };

  // Check if a time matches the open or close time for the selected day
  const getTimeLabel = (hour: number, minute: number, day: string): string => {
    const dayHours = getDayOpeningHours(day);

    if (!dayHours) return formatTimeString(hour, minute);

    if (hour === dayHours.startHour && minute === dayHours.startMinute) {
      return "Open";
    }

    if (hour === dayHours.endHour && minute === dayHours.endMinute) {
      return "Close";
    }

    return formatTimeString(hour, minute);
  };

  useEffect(() => {
    setSelectedDay(props.defaultValue.dayOfWeek);
    setStartHour(props.defaultValue.startHour);
    setStartMinute(props.defaultValue.startMinute || 0);
    setEndHour(props.defaultValue.endHour);
    setEndMinute(props.defaultValue.endMinute || 0);

    // Format the initial time labels
    setStartTimeLabel(
      getTimeLabel(
        props.defaultValue.startHour,
        props.defaultValue.startMinute || 0,
        props.defaultValue.dayOfWeek
      )
    );

    setEndTimeLabel(
      getTimeLabel(
        props.defaultValue.endHour,
        props.defaultValue.endMinute || 0,
        props.defaultValue.dayOfWeek
      )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedDay === "" ||
      !startHour ||
      !endHour ||
      startMinute === undefined ||
      endMinute === undefined
    ) {
      return;
    }

    onChange(index, {
      startHour,
      startMinute,
      endHour,
      endMinute,
      dayOfWeek: selectedDay,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is a parent callback, excluding to avoid re-running on parent re-renders
  }, [selectedDay, startHour, startMinute, endHour, endMinute, index]);

  const days = props.openingHours.map((ohs) => {
    return {
      value: ohs.dayOfWeek,
      label: ohs.dayOfWeek,
    };
  });

  days.push({
    value: "Everyday",
    label: "Everyday",
  });

  const timeOptions = getAvailableExcludedHours(
    props.openingHours,
    selectedDay
  );

  // Filter end time options to only show times after the selected start time
  const getFilteredEndTimeOptions = () => {
    if (!startHour || startMinute === undefined) {
      return timeOptions;
    }

    const startTimeInMinutes = startHour * 60 + startMinute;
    return timeOptions.filter((opt) => {
      const optionTimeInMinutes = opt.value.hour * 60 + opt.value.minute;
      return optionTimeInMinutes > startTimeInMinutes;
    });
  };

  const filteredEndTimeOptions = getFilteredEndTimeOptions();

  return (
    <Flex align="center" margin="m 0 0 0">
      <DropdownWrap>
        <Dropdown
          size="xs"
          selectedOptions={[selectedDay]}
          setSelectedOptions={(value) => setSelectedDay(value[0])}
          renderLabel={() => {
            const selectedDayString = days.find((e) => e.value === selectedDay);
            return selectedDayString ? selectedDayString.label : "None";
          }}
          disableOptionSort
          selectionMode="single"
          options={days.map((day) => ({
            ...day,
            value: day.value.toString(),
          }))}
        />
      </DropdownWrap>

      <DropdownWrap margin="0 0 0 m" size="s">
        <Dropdown
          size="xs"
          selectedOptions={[startTimeLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setStartTimeLabel(newLabel);

            // Find the corresponding time option and update hour/minute
            const selectedOption = timeOptions.find(
              (opt) => opt.label === newLabel
            );
            if (selectedOption) {
              setStartHour(selectedOption.value.hour);
              setStartMinute(selectedOption.value.minute);

              // Reset end time if it's now invalid
              if (endHour !== undefined && endMinute !== undefined) {
                const startTimeInMinutes =
                  selectedOption.value.hour * 60 + selectedOption.value.minute;
                const endTimeInMinutes = endHour * 60 + endMinute;
                if (endTimeInMinutes <= startTimeInMinutes) {
                  setEndHour(undefined);
                  setEndMinute(undefined);
                  setEndTimeLabel("Select Time");
                }
              }
            }
          }}
          renderLabel={() => {
            return startTimeLabel;
          }}
          disableOptionSort
          selectionMode="single"
          options={timeOptions.map((time) => ({
            ...time,
            value: time.label,
          }))}
        />
      </DropdownWrap>

      <Text margin="0 s 0 s">to</Text>
      <DropdownWrap margin="0 s 0 0" size="s">
        <Dropdown
          size="xs"
          selectedOptions={[endTimeLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setEndTimeLabel(newLabel);

            // Find the corresponding time option and update hour/minute
            const selectedOption = timeOptions.find(
              (opt) => opt.label === newLabel
            );
            if (selectedOption) {
              setEndHour(selectedOption.value.hour);
              setEndMinute(selectedOption.value.minute);
            }
          }}
          renderLabel={() => {
            return endTimeLabel;
          }}
          disableOptionSort
          selectionMode="single"
          options={filteredEndTimeOptions.map((time) => ({
            ...time,
            value: time.label,
          }))}
        />
      </DropdownWrap>
      <div
        onClick={() => {
          props.onRemove(props.index);
        }}
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <CancelIcon />
      </div>
    </Flex>
  );
}

interface OpeningHoursProps {
  defaultValue: OpeningHoursInput;
  location: BrandLocationQuery["location"];
  onChange: (index: number, ehs: OpeningHoursInput) => void;
  onRemove: (index: number) => void;
  index: number;
}

function OpeningHours(props: OpeningHoursProps) {
  const { onChange, index } = props;
  const [selectedDay, setSelectedDay] = useState("");
  const [startHour, setStartHour] = useState<number | undefined>();
  const [endHour, setEndHour] = useState<number | undefined>();
  const [startMinute, setStartMinute] = useState<number | undefined>();
  const [endMinute, setEndMinute] = useState<number | undefined>();
  const [startTimeDropdownLabel, setStartTimeDropdownLabel] =
    useState<string>("");
  const [endTimeDropdownLabel, setEndTimeDropdownLabel] = useState<string>("");

  // Helper to format time for display in Dropdown label
  const formatTimeDisplay = (hour?: number, minute?: number): string => {
    if (hour === undefined || minute === undefined) return "Select Time";
    const hrStr = hour < 10 ? `0${hour}` : `${hour}`;
    const minStr = minute === 0 ? "00" : `${minute}`; // minute will be 0 or 30
    return `${hrStr}:${minStr}`;
  };

  // Helper to parse "HH:MM" string label back to hour and minute
  const parseTimeLabel = (
    timeLabel: string
  ): { hour: number; minute: number } => {
    const parts = timeLabel.split(":");
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    return { hour, minute };
  };

  useEffect(() => {
    setSelectedDay(props.defaultValue.dayOfWeek);
    setStartHour(props.defaultValue.startHour);
    setStartMinute(props.defaultValue.startMinute);
    setEndHour(props.defaultValue.endHour);
    setEndMinute(props.defaultValue.endMinute);

    setStartTimeDropdownLabel(
      formatTimeDisplay(
        props.defaultValue.startHour,
        props.defaultValue.startMinute
      )
    );
    setEndTimeDropdownLabel(
      formatTimeDisplay(
        props.defaultValue.endHour,
        props.defaultValue.endMinute
      )
    );
  }, [props.defaultValue]);

  useEffect(() => {
    if (
      selectedDay === "" ||
      startHour === undefined ||
      endHour === undefined ||
      startMinute === undefined ||
      endMinute === undefined
    ) {
      return;
    }

    // Only call onChange if end time is after start time
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;

    if (endTimeInMinutes > startTimeInMinutes) {
      onChange(index, {
        startHour,
        startMinute,
        endHour,
        endMinute,
        dayOfWeek: selectedDay,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is a parent callback, excluding to avoid re-running on parent re-renders
  }, [selectedDay, startHour, startMinute, endHour, endMinute, index]);

  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const timeOptionsForDropdown = getAvailableHours().map((opt) => ({
    label: opt.label,
    value: opt.label,
  }));

  // Filter end time options to only show times after the selected start time
  const getFilteredEndTimeOptions = () => {
    if (startHour === undefined || startMinute === undefined) {
      return timeOptionsForDropdown;
    }

    const startTimeInMinutes = startHour * 60 + startMinute;
    return timeOptionsForDropdown.filter((opt) => {
      const { hour, minute } = parseTimeLabel(opt.label);
      const optionTimeInMinutes = hour * 60 + minute;
      return optionTimeInMinutes > startTimeInMinutes;
    });
  };

  const filteredEndTimeOptions = getFilteredEndTimeOptions();

  return (
    <Flex align="center" margin="m 0 0 0">
      <DropdownWrap>
        <Dropdown
          size="xs"
          selectedOptions={[selectedDay]}
          setSelectedOptions={(value) => setSelectedDay(value[0])}
          renderLabel={() => {
            const selectedDayString = weekDays.find((e) => e === selectedDay);
            return selectedDayString ? selectedDayString : "None";
          }}
          disableOptionSort
          selectionMode="single"
          options={weekDays.map((day) => ({
            label: day,
            value: day,
          }))}
        />
      </DropdownWrap>

      <DropdownWrap margin="0 0 0 m" size="s">
        <Dropdown
          size="xs"
          selectedOptions={[startTimeDropdownLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setStartTimeDropdownLabel(newLabel);
            const { hour, minute } = parseTimeLabel(newLabel);
            setStartHour(hour);
            setStartMinute(minute);

            // Reset end time if it's now invalid
            if (endHour !== undefined && endMinute !== undefined) {
              const startTimeInMinutes = hour * 60 + minute;
              const endTimeInMinutes = endHour * 60 + endMinute;
              if (endTimeInMinutes <= startTimeInMinutes) {
                setEndHour(undefined);
                setEndMinute(undefined);
                setEndTimeDropdownLabel("Select Time");
              }
            }
          }}
          renderLabel={() => startTimeDropdownLabel || "Select Time"}
          disableOptionSort
          selectionMode="single"
          options={timeOptionsForDropdown}
        />
      </DropdownWrap>

      <Text margin="0 s 0 s">to</Text>

      <DropdownWrap margin="0 m 0 0" size="s">
        <Dropdown
          margin="0 m 0 0"
          size="xs"
          selectedOptions={[endTimeDropdownLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setEndTimeDropdownLabel(newLabel);
            const { hour, minute } = parseTimeLabel(newLabel);
            setEndHour(hour);
            setEndMinute(minute);
          }}
          renderLabel={() => endTimeDropdownLabel || "Select Time"}
          disableOptionSort
          selectionMode="single"
          options={filteredEndTimeOptions}
        />
      </DropdownWrap>

      <div
        onClick={() => {
          props.onRemove(props.index);
        }}
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <CancelIcon />
      </div>
    </Flex>
  );
}

function getAvailableHours(): TimeOption[] {
  const hours = [];

  for (let i = 0; i < 24; i++) {
    hours.push({
      label: `${i < 10 ? "0" : ""}${i}:00`,
      value: {
        hour: i,
        minute: 0,
      },
    });

    // Only add :30 slots up to 23:30
    if (i < 23 || i === 23) {
      hours.push({
        label: `${i < 10 ? "0" : ""}${i}:30`,
        value: {
          hour: i,
          minute: 30,
        },
      });
    }
  }

  return hours;
}

const TrashWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: ${(p) => p.theme.spacing.m};

  &:hover {
    svg {
      filter: brightness(0.25);
      cursor: pointer;
    }
  }
`;

const AbsoluteWrap = styled.div`
  position: absolute;
  top: 0;
  right: ${(p) => p.theme.spacing.m};
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
`;

// Add TimeOption interface back
interface TimeOption {
  label: string;
  value: {
    hour: number;
    minute: number;
  };
}

// Add the getAvailableExcludedHours function back
function getAvailableExcludedHours(
  ohs: OpeningHoursInput[],
  dayOfWeek: string
): TimeOption[] {
  const values: TimeOption[] = [];

  const addAvailableHours = (
    startHour: number,
    endHour: number,
    startMinute: number,
    endMinute: number
  ) => {
    // Add special "Open" option at the beginning
    values.push({
      label: "Open",
      value: {
        hour: startHour,
        minute: startMinute,
      },
    });

    // Start at next 30-min increment after opening time
    let nextHour = startHour;
    let nextMinute = startMinute;

    // Advance to next time slot (30 min increments)
    if (nextMinute === 0) {
      nextMinute = 30;
    } else {
      nextHour += 1;
      nextMinute = 0;
    }

    // Generate all intermediate time options
    while (
      nextHour < endHour ||
      (nextHour === endHour && nextMinute < endMinute)
    ) {
      values.push({
        label: `${nextHour < 10 ? "0" : ""}${nextHour}:${
          nextMinute === 0 ? "00" : nextMinute
        }`,
        value: {
          hour: nextHour,
          minute: nextMinute,
        },
      });

      // Move to next 30-min slot
      if (nextMinute === 0) {
        nextMinute = 30;
      } else {
        nextHour += 1;
        nextMinute = 0;
      }
    }

    // Add "Close" option at the end
    values.push({
      label: "Close",
      value: {
        hour: endHour,
        minute: endMinute,
      },
    });
  };

  if (dayOfWeek === "Everyday") {
    let earliestStartHour = 23;
    let earliestStartMinute = 59;
    let latestEndHour = 0;
    let latestEndMinute = 0;

    for (const oh of ohs) {
      if (
        oh.startHour < earliestStartHour ||
        (oh.startHour === earliestStartHour &&
          oh.startMinute < earliestStartMinute)
      ) {
        earliestStartHour = oh.startHour;
        earliestStartMinute = oh.startMinute;
      }

      if (
        oh.endHour > latestEndHour ||
        (oh.endHour === latestEndHour && oh.endMinute > latestEndMinute)
      ) {
        latestEndHour = oh.endHour;
        latestEndMinute = oh.endMinute;
      }
    }

    addAvailableHours(
      earliestStartHour,
      latestEndHour,
      earliestStartMinute,
      latestEndMinute
    );
  } else {
    let dayOpeningHours: OpeningHoursInput | undefined;

    for (const oh of ohs) {
      if (oh.dayOfWeek === dayOfWeek) {
        dayOpeningHours = oh;
        break;
      }
    }

    if (dayOpeningHours) {
      addAvailableHours(
        dayOpeningHours.startHour,
        dayOpeningHours.endHour,
        dayOpeningHours.startMinute,
        dayOpeningHours.endMinute
      );
    } else {
      // If no opening hours for selected day, add standard time options
      for (let i = 0; i <= 23; i++) {
        values.push({
          label: `${i < 10 ? "0" : ""}${i}:00`,
          value: {
            hour: i,
            minute: 0,
          },
        });
        values.push({
          label: `${i < 10 ? "0" : ""}${i}:30`,
          value: {
            hour: i,
            minute: 30,
          },
        });
      }
    }
  }

  return values;
}

const DropdownWrap = styled(View)<{ size?: "s" }>`
  min-width: ${(p) => (p.size === "s" ? "80px" : "130px")};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: ${(p) => (p.size === "s" ? "50px" : "80px")};
  }
`;
